import React, { useEffect, useState } from "react";
import axios from "axios";
import Pagination from "../../Pagination";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import MetricNavbar from "./MetricNavbar";
import * as XLSX from "xlsx";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

function JDPerformanceDashboard() {
  const [jdMetrics, setJdMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJD, setSelectedJD] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("performance_score");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const metricOptions = {
    performance_score: "JD Performance Score",
  };

  const metricColors = {
    performance_score: "#008080",
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      const storedClient = localStorage.getItem("client");

      // Convert stored values to numbers
      const client_id = storedClient ? Number(storedClient) : null;

      // Debugging: Log retrieved values
      console.log("Fetching data with:", { client_id });

      // Ensure client_id and user_id are valid before making the request
      if (!client_id) {
        console.error("Missing client_id . Please log in again.");
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/screener/api-metrics/?client_id=${client_id}`
        );
        const backendData = response.data;

        const mergedJdMetrics = backendData.jd_performance_scores.map(
          (item) => ({
            jd_id: item.jd,
            performance_score: parseFloat((item.score * 100).toFixed(2)),
            // fill_rate: parseFloat((backendData.jd_fill_rate * 100).toFixed(2)),
            // avg_time_to_close: backendData.avg_time_to_close_jd,
            // resume_quality_trend: parseFloat(
            //   backendData.resume_to_hire_conversion_rate * 100
            // ).toFixed(2),
          })
        );

        setJdMetrics(mergedJdMetrics);
      } catch (err) {
        setError("Failed to fetch JD metrics.");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const filteredMetrics = jdMetrics.filter((m) => {
    return (
      m.jd_id.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (selectedJD === "" || m.jd_id === selectedJD)
    );
  });

  const exportToExcel = () => {
    const exportData = jdMetrics.map((item) => ({
      "JD ID": item.jd_id,
      "Performance Score": item.performance_score,
    }));

    // Step 2: Compute total
    const totalScore = jdMetrics.reduce(
      (sum, row) => sum + (parseFloat(row.performance_score) || 0),
      0
    );

    const totalRow = {
      "JD ID": "Total",
      "Performance Score": totalScore,
    };

    // Step 3: Append total row
    exportData.push(totalRow);

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Automatically adjust column widths based on content
    const columnWidths = Object.keys(exportData[0]).map((key) => {
      const maxLength = Math.max(
        key.length,
        ...exportData.map((row) => String(row[key]).length)
      );
      return { wch: maxLength + 2 }; // add padding
    });

    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "JD Performance");
    XLSX.writeFile(workbook, "JD_Performance_Report.xlsx");
  };

  const metricTotals = {};
  Object.keys(metricOptions).forEach((key) => {
    metricTotals[key] = filteredMetrics.reduce(
      (sum, row) => sum + (parseFloat(row[key]) || 0),
      0
    );
  });

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleJDFilter = (e) => {
    setSelectedJD(e.target.value);
    setCurrentPage(1);
  };

  // const handleMetricChange = (e) => {
  //   setSelectedMetric(e.target.value);
  // };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMetrics.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMetrics.length / itemsPerPage);

  const chartData = {
    labels: filteredMetrics.map((m) => m.jd_id),
    datasets: [
      {
        label: metricOptions[selectedMetric],
        data: filteredMetrics.map((m) => m[selectedMetric]),
        backgroundColor: metricColors[selectedMetric],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">&nbsp;Loading JD metrics.... </h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-danger text-center py-5">
        <h4>Error: {error}</h4>
      </div>
    );
  }

  return (
    <>
      <MetricNavbar />
      <div className="container py-4">
        <h2 className="text-center mb-4 font_header">
          JD Performance Dashboard
        </h2>

        <div className="row mb-4">
          <div className="col-md-3">
            <label htmlFor="jdSelect" className="form-label fw-semibold">
              Filter by JD:
            </label>
            <select
              id="jdSelect"
              className="form-select border-dark"
              value={selectedJD}
              onChange={handleJDFilter}
            >
              <option value="">All JDs</option>
              {jdMetrics.map((m, index) => (
                <option key={index} value={m.jd_id}>
                  {m.jd_id}
                </option>
              ))}
            </select>
          </div>

          {/* <div className="col-md-3">
            <label htmlFor="metricSelect" className="form-label fw-semibold">
              Select Metric:
            </label>
            <select
              id="metricSelect"
              className="form-select border-dark"
              value={selectedMetric}
              onChange={handleMetricChange}
            >
              {Object.entries(metricOptions).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div> */}

          <div className="col-md-3">
            <label htmlFor="searchInput" className="form-label fw-semibold">
              Search JD ID:
            </label>
            <input
              type="search"
              id="searchInput"
              className="form-control border-dark"
              placeholder="Enter JD ID..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          <div className="col-md text-lg-end mt-3">
            <button className="btn btn-outline-info" onClick={exportToExcel}>
              Download
            </button>
          </div>
        </div>

        <div className="mb-5">
          <h4 className="text-center mb-3 font_header">
            {metricOptions[selectedMetric]} by JD
          </h4>
          <Bar data={chartData} options={chartOptions} />
        </div>

        <div className="table-responsive table-wrapper text-center">
          <table className="table table-bordered table-hover border-dark">
            <thead className="text-center tb_header border-dark">
              <tr>
                <th>JD ID</th>
                <th>Performance Score</th>
                {/* <th>Fill Rate (%)</th>
                <th>Avg. Time to Close (days)</th>
                <th>Resume Quality Trend (%)</th> */}
              </tr>
            </thead>
            <tbody className="text-center">
              {currentItems.map((jd, index) => (
                <tr key={index}>
                  <td>{jd.jd_id}</td>
                  <td>{jd.performance_score}</td>
                  {/* <td>{jd.fill_rate}</td>
                  <td>{jd.avg_time_to_close}</td>
                  <td>{jd.resume_quality_trend}</td> */}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: "bold", backgroundColor: "#f9f9f9" }}>
                <td>Total</td>
                {Object.keys(metricOptions).map((key) => (
                  <td key={key}>{metricTotals[key]}</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginate={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
}

export default JDPerformanceDashboard;
